import React, { useState, useEffect } from 'react';
import './DeliverySettings.css';
import { getDeliverySettings, saveDeliverySettings } from '../../../utils/deliverySettings';

const DeliverySettings = ({ onSave, onChanges }) => {
  const [formData, setFormData] = useState({
    deliveryFee: 99,
    freeDeliveryAbove: 800,
    minOrderAmount: 300,
    deliveryTimeMin: 30,
    deliveryTimeMax: 40
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Load existing delivery settings on component mount
  useEffect(() => {
    loadDeliverySettings();
  }, []);

  const loadDeliverySettings = async () => {
    try {
      setFetching(true);
      const { data, error } = await getDeliverySettings();
      
      if (error) {
        console.error('Error loading delivery settings:', error);
        setFetching(false);
        return;
      }

      if (data) {
        setFormData({
          deliveryFee: data.delivery_fee || 99,
          freeDeliveryAbove: data.free_delivery_above || 800,
          minOrderAmount: data.min_order_amount || 300,
          deliveryTimeMin: data.delivery_time_min || 30,
          deliveryTimeMax: data.delivery_time_max || 40
        });
      }
    } catch (error) {
      console.error('Error loading delivery settings:', error);
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
    onChanges();
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      console.log('Saving delivery settings to Supabase...');
      
      const { data, error } = await saveDeliverySettings(formData);
      
      if (error) {
        console.error('❌ Error saving delivery settings:', error);
        const errorMessage = error.message || 'Failed to save delivery settings. Please try again.';
        alert(`Error: ${errorMessage}`);
        return;
      }

      if (!data) {
        console.error('❌ No data returned from save operation');
        alert('Failed to save delivery settings. No data returned.');
        return;
      }

      console.log('✅ Delivery settings saved successfully to Supabase!');
      onSave();
    } catch (error) {
      console.error('❌ Unexpected error saving delivery settings:', error);
      alert(`Failed to save delivery settings: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="settings-section">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading delivery settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2 className="section-title">Delivery Settings</h2>
        <p className="section-subtitle">Configure delivery options</p>
        <div className="section-divider"></div>
      </div>

      <div className="form-content">
        <div className="form-group">
          <label className="form-label">
            Delivery Charge (PKR) <span className="required">*</span>
          </label>
          <div className="input-with-prefix">
            <span className="input-prefix">PKR</span>
            <input
              type="number"
              name="deliveryFee"
              value={formData.deliveryFee}
              onChange={handleChange}
              className="form-input"
              min="0"
            />
          </div>
          <div className="helper-text">Flat delivery charge per order</div>
        </div>

        <div className="form-group">
          <label className="form-label">Free Delivery Above (PKR)</label>
          <div className="input-with-prefix">
            <span className="input-prefix">PKR</span>
            <input
              type="number"
              name="freeDeliveryAbove"
              value={formData.freeDeliveryAbove}
              onChange={handleChange}
              className="form-input"
              min="0"
            />
          </div>
          <div className="helper-text">Orders above this amount get free delivery</div>
        </div>

        <div className="form-group">
          <label className="form-label">
            Minimum Order Amount (PKR) <span className="required">*</span>
          </label>
          <div className="input-with-prefix">
            <span className="input-prefix">PKR</span>
            <input
              type="number"
              name="minOrderAmount"
              value={formData.minOrderAmount}
              onChange={handleChange}
              className="form-input"
              min="0"
            />
          </div>
          <div className="helper-text">Customers must order at least this amount</div>
        </div>

        <div className="form-group">
          <label className="form-label">
            Estimated Delivery Time (minutes) <span className="required">*</span>
          </label>
          <div className="time-range-inputs">
            <input
              type="number"
              name="deliveryTimeMin"
              value={formData.deliveryTimeMin}
              onChange={handleChange}
              className="form-input time-input-small"
              min="0"
            />
            <span className="time-separator">-</span>
            <input
              type="number"
              name="deliveryTimeMax"
              value={formData.deliveryTimeMax}
              onChange={handleChange}
              className="form-input time-input-small"
              min="0"
            />
            <span className="time-suffix">minutes</span>
          </div>
        </div>

        <div className="form-actions">
          <button 
            className="save-btn" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliverySettings;

